import React, { useEffect, useState } from 'react';
import { Table, Button, Alert } from 'react-bootstrap';
import { fetchPortsList, deletePort } from './services/api';
import { useNavigate } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { Port } from './types';

const PortsList: React.FC = () => {
  const [ports, setPorts] = useState<Port[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    fetchPortsList()
      .then(response => {
        const ports = Array.isArray(response.data) ? response.data : [];
        setPorts(ports);
      })
      .catch(() => setError('Failed to fetch ports'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this Location?')) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    setDeletingId(id);
    try {
      await deletePort(id);
      setPorts(ports.filter(p => p.id !== id));
      setSuccess('Location deleted successfully.');
    } catch {
      setError('Failed to delete Location');
    }
    setLoading(false);
    setDeletingId(null);
  };

  return (
    <div className="p-4 p-md-5">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Locations List</h2>
        <Button variant="primary" onClick={() => navigate('/ports/create')}>
          <i className="fas fa-plus me-2"></i> Create Location
        </Button>
      </div>
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
      <div className="table-responsive">
        <Table striped bordered hover>
          <colgroup>
            <col />
            <col />
            <col style={{ width: '140px' }} />
          </colgroup>
          <thead>
            <tr>
              <th>Location</th>
              <th>State</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  <td><Skeleton /></td>
                  <td><Skeleton /></td>
                  <td><Skeleton width={70} /></td>
                </tr>
              ))
            ) : ports.length === 0 ? (
              <tr><td colSpan={3} className="text-center">No ports found</td></tr>
            ) : (
              ports.map(port => (
                <tr key={port.id}>
                  <td>{port.name}</td>
                  <td>{port.location}</td>
                  <td>
                    <button className="btn btn-sm btn-outline-primary me-2" onClick={() => navigate(`/ports/edit/${port.id}`, { state: { port } })}>
                      <i className="fas fa-edit"></i>
                    </button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(port.id)} disabled={deletingId === port.id}>
                      {deletingId === port.id ? <span className="spinner-border spinner-border-sm" /> : <i className="fas fa-trash"></i>}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default PortsList; 